/**
 * Customer-Auth-State auf der Subdomain.
 *
 * - Token + User-Profil im localStorage (per Subdomain).
 * - Re-validiert on mount via GET /public/customer/me.
 * - Backend ist die einzige Quelle für die User-Daten — wir cachen nur lokal.
 */
import { useEffect, useState } from "react";
import { API_BASE } from "./api";

export type CustomerAddress = {
  street: string;
  postal_code: string;
  city: string;
  country: string;
};

export type CustomerSelf = {
  id: string;
  wp_user_id: number | null;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  company: string | null;
  tax_id: string | null;
  status: string;

  billing_street: string | null;
  billing_postal_code: string | null;
  billing_city: string | null;
  billing_country: string | null;
  shipping_same_as_billing: boolean;
  shipping_street: string | null;
  shipping_postal_code: string | null;
  shipping_city: string | null;
  shipping_country: string | null;

  agent_id: string | null;
  invite_code: string | null;

  created_at: string;
  approved_at: string | null;
};

export type CustomerLoginResponse = {
  token: string;
  customer: CustomerSelf;
};

export type CustomerRegisterPayload = {
  email: string;
  accepted_tos: boolean;
  vorname?: string;
  nachname?: string;
  anrede?: string;
  firma?: string;
  telefon?: string;
  ustid?: string;
  kundentyp?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  invite_code?: string;
  extra?: Record<string, unknown>;
};

export type CustomerRegisterResponse = {
  ok: boolean;
  message: string;
  wp_post_id: number | null;
};

const TOKEN_KEY = "dp-customer-token";
const USER_KEY = "dp-customer-user";

export function getCustomerToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredCustomer(): CustomerSelf | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CustomerSelf;
  } catch {
    return null;
  }
}

export function setCustomerSession(token: string, user: CustomerSelf): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  window.dispatchEvent(new CustomEvent("dp-customer-auth-changed"));
}

export function clearCustomerSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  window.dispatchEvent(new CustomEvent("dp-customer-auth-changed"));
}

// API calls

export async function customerLogin(
  email: string,
  password: string,
): Promise<CustomerLoginResponse> {
  const res = await fetch(`${API_BASE}/public/customer/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    let detail = "Login fehlgeschlagen";
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<CustomerLoginResponse>;
}

export async function customerRegister(
  payload: CustomerRegisterPayload,
): Promise<CustomerRegisterResponse> {
  const res = await fetch(`${API_BASE}/public/customer/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const detail =
      (body && (body.detail || body.message)) ||
      `Anmeldung fehlgeschlagen (${res.status})`;
    throw new Error(detail);
  }
  return body as CustomerRegisterResponse;
}

// ============================================================
// Order-Endpoints (Welle 6C — Checkout)
// ============================================================

export type CartItemInput = {
  wc_product_id: number;
  qty: number;
  variation_id?: number | null;
};

export type OrderItemSnapshot = {
  wc_product_id: number;
  variation_id?: number | null;
  name: string;
  qty: number;
  unit_price_cents: number;
  total_cents: number;
  sku?: string;
  image_url?: string | null;
  permalink?: string | null;
};

export type CheckoutPreview = {
  items: OrderItemSnapshot[];
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  min_order_value_cents: number;
  min_order_value_reached: boolean;
  free_shipping_threshold_cents: number;
  free_shipping_reached: boolean;
  warnings: string[];
};

export type OrderResponse = {
  id: string;
  order_number: string;
  customer_id: string;
  agent_id: string | null;
  items: OrderItemSnapshot[];
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  payment_method: string;
  payment_status: string;
  paid_at: string | null;
  billing_address: Record<string, unknown>;
  shipping_address: Record<string, unknown>;
  customer_note: string | null;
  status: string;
  tracking_number: string | null;
  tracking_carrier: string | null;
  created_at: string;
  updated_at: string;
};

export type BankDetails = {
  holder: string;
  iban: string;
  bic: string;
  bank_name: string;
};

export type OrderConfirmationResponse = {
  order: OrderResponse;
  bank_details: BankDetails;
  payment_terms_days: number;
  payment_due_date: string;
  email_sent: boolean;
};

async function fetchAuthed<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getCustomerToken();
  if (!token) throw new Error("Nicht eingeloggt");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...((init.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) clearCustomerSession();
    const detail =
      (body && (body.detail || body.message)) ||
      `Anfrage fehlgeschlagen (${res.status})`;
    throw new Error(detail);
  }
  return body as T;
}

export async function previewCheckout(items: CartItemInput[]): Promise<CheckoutPreview> {
  return fetchAuthed<CheckoutPreview>("/public/customer/checkout/preview", {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

export async function submitOrder(
  items: CartItemInput[],
  customer_note?: string,
): Promise<OrderConfirmationResponse> {
  return fetchAuthed<OrderConfirmationResponse>("/public/customer/orders", {
    method: "POST",
    body: JSON.stringify({ items, customer_note }),
  });
}

export async function listMyOrders(): Promise<{ items: OrderResponse[] }> {
  return fetchAuthed<{ items: OrderResponse[] }>("/public/customer/orders");
}

export async function getOrderByNumber(
  orderNumber: string,
): Promise<OrderConfirmationResponse> {
  return fetchAuthed<OrderConfirmationResponse>(
    `/public/customer/orders/${encodeURIComponent(orderNumber)}`,
  );
}

// ============================================================

export async function fetchCustomerMe(): Promise<CustomerSelf | null> {
  const token = getCustomerToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/public/customer/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401 || res.status === 403) {
      clearCustomerSession();
      return null;
    }
    if (!res.ok) return null;
    return (await res.json()) as CustomerSelf;
  } catch {
    return null;
  }
}

// Hook für Components

export function useCustomer() {
  const [customer, setCustomer] = useState<CustomerSelf | null>(() =>
    getStoredCustomer(),
  );
  const [loading, setLoading] = useState<boolean>(() => Boolean(getCustomerToken()));

  useEffect(() => {
    const onChange = () => setCustomer(getStoredCustomer());
    window.addEventListener("dp-customer-auth-changed", onChange);
    return () => window.removeEventListener("dp-customer-auth-changed", onChange);
  }, []);

  useEffect(() => {
    const token = getCustomerToken();
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchCustomerMe()
      .then((fresh) => {
        if (cancelled) return;
        if (fresh) {
          localStorage.setItem(USER_KEY, JSON.stringify(fresh));
          setCustomer(fresh);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { customer, loading };
}
